import { useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/v2/commonComponents/Button';
import {
  useGetUser,
  useOfferInfo,
  useSubscriptionInfo,
  useSubscriptionPlans,
} from 'src/hooks';
import { AppRoutes, UserSettingsNestedRoutes, UserTier } from 'src/types';
import { LoadingOverlay } from 'src/pages/PaymentBetaPage/components/LoadingOverlay';
import { CancelSubscriptionDifferentPlanItem } from '../CancelSubscriptionDifferentPlanItem';
import styles from './CancelSubscriptionDifferentPlan.module.scss';
import { CancelSubscriptionSteps } from 'src/v2/pages/subscription/components/CancelSubscription';
import { AVAILABLE_FEATURES_LIST } from 'src/constants';

const isABtestPaymentPage = AVAILABLE_FEATURES_LIST.ABtestPaymentPage;

interface CancelSubscriptionDifferentPlanProps {
  onChangeStep: (value: number) => void;
}

export const CancelSubscriptionDifferentPlan = ({
  onChangeStep,
}: CancelSubscriptionDifferentPlanProps) => {
  const navigate = useNavigate();
  const { tier_id, isTeamsTier } = useGetUser();
  const {
    subscriptionInfo,
    isSubscriptionInfoFetching,
    isSubscriptionInfoLoading,
    isAppleOrAmpPayment,
  } = useSubscriptionInfo();
  const { threeMonthsFreeOffer } = useOfferInfo();

  const { plans, isPlansLoading } = useSubscriptionPlans({
    group: isTeamsTier ? 'teams' : 'individual',
    // TODO: check if we need to pass seat_count
    // seat_count: isTeamsTier
    //   ? isTeamsTier
    //     ? subscriptionInfo?.seat_count
    //     : undefined
    //   : undefined,
  });

  const hideChangePlanButton =
    subscriptionInfo?.has_scheduled_downgrade ||
    subscriptionInfo?.has_scheduled_plan_change ||
    subscriptionInfo?.processing_plan_change ||
    isAppleOrAmpPayment;

  useLayoutEffect(() => {
    if (!subscriptionInfo || subscriptionInfo?.is_cancelled) {
      navigate(
        `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`,
      );
    }
  }, [subscriptionInfo, navigate]);

  const planSections: UserTier[] = useMemo(() => {
    if (isABtestPaymentPage) {
      return [UserTier.PRO, UserTier.ULTRA, UserTier.SUPERNINJA_ULTRA].filter(
        (plan) => plan !== tier_id,
      );
    }

    return AVAILABLE_FEATURES_LIST.newSuperNinjaPlans
      ? [UserTier.STANDARD, UserTier.ULTRA, UserTier.SUPERNINJA_ULTRA].filter(
          (plan) => plan !== tier_id,
        )
      : [UserTier.STANDARD, UserTier.PRO, UserTier.ULTRA].filter(
          (plan) => plan !== tier_id,
        );
  }, [tier_id]);

  const handleGoToPaymentPageClick = () => {
    navigate(AppRoutes.PAYMENT);
  };

  const handleDeclineClick = () => {
    onChangeStep(
      threeMonthsFreeOffer?.eligible
        ? CancelSubscriptionSteps.COMPLEMENTARY_OFFER
        : CancelSubscriptionSteps.SELECT_REASON,
    );
  };

  if (
    isSubscriptionInfoFetching ||
    isSubscriptionInfoLoading ||
    isPlansLoading
  ) {
    return <LoadingOverlay label="Loading..." />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Did your needs change? Try a different plan</h2>
          <p className={styles.subTitle}>
            Unlimited Tasks, Premium AI Models,
            <br />
            One Subscription
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.planWrapper}>
            {planSections.map((item) => (
              <CancelSubscriptionDifferentPlanItem
                item={item}
                plans={plans}
                key={item}
              />
            ))}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        {!hideChangePlanButton && (
          <Button onClick={handleGoToPaymentPageClick}>Change plan</Button>
        )}
        <Button color="secondary-outline" onClick={handleDeclineClick}>
          Continue cancellation
        </Button>
      </div>
    </div>
  );
};
